import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { BookX, ClipboardCheck } from "lucide-react";
import { useBranch } from "../context/BranchContext";
import { useSettings } from "../context/SettingsContext";
import { showOsNotification } from "../lib/notify";

const POLL_MS = 60000;

export function useBookStatus() {
  const { activeBranch, isAllView } = useBranch();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (isAllView) { setStatus(null); return; }
    let alive = true;
    const tick = () => http.get("/reconciliation/status").then((r) => alive && setStatus(r.data)).catch(() => {});
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [activeBranch, isAllView]);
  const needsClosing = !!status && ((status.overdue) || (!status.last_closing_at && status.tx_since > 0));

  // Fire a reminder notification at most once per branch per day.
  useEffect(() => {
    if (!needsClosing || !activeBranch) return;
    const key = `emc_notif_book_${activeBranch}_${new Date().toISOString().slice(0, 10)}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* ignore */ }
    showOsNotification("Saatnya Tutup Buku", {
      body: `${status?.tx_since || 0} transaksi menunggu ditutup di cabang ini.`,
      tag: `book-${activeBranch}`,
      url: "/admin/shift",
    });
  }, [needsClosing, activeBranch, status?.tx_since]);

  return { status, needsClosing };
}

export function BookClosingBell({ onCount } = {}) {
  const { status, needsClosing } = useBookStatus();
  useEffect(() => { onCount?.(needsClosing ? (status?.tx_since || 1) : 0); }, [needsClosing, status]);
  if (!needsClosing) return null;
  return (
    <Link to="/admin/shift" data-testid="book-closing-bell"
      title={`Tutup buku belum dilakukan ${status.hours_since ? `${Math.floor(status.hours_since)} jam` : ""} · ${status.tx_since} transaksi belum ditutup`}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white/60 hover:bg-rose-50 text-rose-600 animate-pulse transition">
      <BookX size={18} />
      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">{status.tx_since > 99 ? "99+" : status.tx_since}</span>
    </Link>
  );
}

export function BookClosingBanner() {
  const { status, needsClosing } = useBookStatus();
  const { fmtDateTime } = useSettings();
  if (!needsClosing) return null;
  return (
    <div className="mb-6 rounded-lg border-2 border-rose-300 bg-rose-50 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="book-closing-banner">
      <BookX size={22} className="text-rose-600 shrink-0" />
      <div className="flex-1">
        <div className="font-semibold text-rose-800">Buku belum ditutup {status.last_closing_at ? `— sudah ${Math.floor(status.hours_since)} jam sejak tutup buku terakhir (${fmtDateTime(status.last_closing_at)})` : "— belum pernah tutup buku di cabang ini"}</div>
        <div className="text-sm text-rose-700">{status.tx_since} transaksi menunggu ditutup. Tutup buku berlaku 24 jam; lakukan di akhir jam operasional agar uang fisik & sistem tetap cocok.</div>
      </div>
      <Link to="/admin/shift" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700" data-testid="book-closing-banner-cta">
        <ClipboardCheck size={15} /> Tutup Buku Sekarang
      </Link>
    </div>
  );
}
